(function (root, factory) {
  var api = factory();

  if (typeof module === "object" && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.AcousticCalculatorProductTiers = api;
  }
})(typeof window !== "undefined" ? window : null, function () {
  "use strict";

  var BANDS = ["250 Hz", "500 Hz", "1000 Hz", "2000 Hz", "4000 Hz"];
  var NRC_BANDS = ["250 Hz", "500 Hz", "1000 Hz", "2000 Hz"];
  var TIER_BANDS = ["500 Hz", "1000 Hz", "2000 Hz", "4000 Hz"];

  function calculateNrc(product) {
    var footprint = Number(product && product.footprint);
    var absorption = (product && product.absorption) || {};
    var total = NRC_BANDS.reduce(function (sum, band) {
      return sum + Number(absorption[band] || 0) / footprint;
    }, 0);
    var rounded = Math.round(total / NRC_BANDS.length / 0.05) * 0.05;

    return Number(rounded.toFixed(2));
  }

  function normalizeCatalog(catalog) {
    var normalized = [];
    var productsById = Object.create(null);

    ["wall", "ceiling"].forEach(function (surface) {
      var products =
        catalog && Array.isArray(catalog[surface]) ? catalog[surface] : [];

      products.forEach(function (sourceProduct) {
        var existing = productsById[sourceProduct.id];

        if (existing) {
          if (existing.surfaces.indexOf(surface) === -1) {
            existing.surfaces.push(surface);
          }
          return;
        }

        var product = Object.assign({}, sourceProduct, {
          absorption: Object.assign({}, sourceProduct.absorption),
          surfaces: [surface],
          nrc: calculateNrc(sourceProduct),
        });

        productsById[product.id] = product;
        normalized.push(product);
      });
    });

    return normalized;
  }

  function buildTierQuantities(premiumQuantity) {
    var numericQuantity = Number(premiumQuantity);
    var premium = Number.isFinite(numericQuantity)
      ? Math.max(0, Math.ceil(numericQuantity))
      : 0;

    return {
      starter: Math.ceil(premium * 0.4),
      standard: Math.ceil(premium * 0.7),
      premium: premium,
    };
  }

  function buildTierResults(
    premiumQuantity,
    calculateRt60,
    referenceRt60ByTier,
  ) {
    var quantities = buildTierQuantities(premiumQuantity);

    return Object.keys(quantities).reduce(function (results, tier) {
      var quantity = quantities[tier];
      var rt60ByBand = calculateRt60(quantity);
      var maximum = Math.max.apply(
        Math,
        TIER_BANDS.map(function (band) {
          return Number(rt60ByBand[band]);
        }),
      );

      results[tier] = {
        quantity: quantity,
        rt60:
          referenceRt60ByTier &&
          Number.isFinite(Number(referenceRt60ByTier[tier]))
            ? Number(Number(referenceRt60ByTier[tier]).toFixed(2))
            : Number(maximum.toFixed(2)),
      };
      return results;
    }, {});
  }

  function buildSelectionResult(
    maxQuantity,
    targetRt60,
    calculateRt60,
    referenceTiers,
  ) {
    var upper = Math.max(0, Math.ceil(Number(maxQuantity) || 0));
    var target = Number(targetRt60);
    var matchingTier = ["premium", "standard", "starter"]
      .map(function (key) {
        return referenceTiers && referenceTiers[key];
      })
      .find(function (tier) {
        return (
          tier &&
          Number.isFinite(Number(tier.rt60)) &&
          Math.abs(Number(tier.rt60) - target) < 0.000001
        );
      });

    if (matchingTier) {
      return {
        quantity: Math.max(0, Math.ceil(Number(matchingTier.quantity) || 0)),
        rt60: Number(Number(matchingTier.rt60).toFixed(2)),
      };
    }

    function maximumRt60(quantity) {
      var rt60ByBand = calculateRt60(quantity);
      return Number(
        Math.max.apply(
          Math,
          TIER_BANDS.map(function (band) {
            return Number(rt60ByBand[band]);
          }),
        ).toFixed(2),
      );
    }

    var low = 0;
    var high = upper;
    while (low < high) {
      var middle = Math.floor((low + high) / 2);
      if (maximumRt60(middle) <= target) high = middle;
      else low = middle + 1;
    }

    return {
      quantity: low,
      rt60: Number(maximumRt60(low).toFixed(2)),
    };
  }

  function normalizeBuildRows(settings, catalog) {
    var sourceRows;
    var productsById = (catalog || []).reduce(function (byId, product) {
      if (product && product.id) byId[product.id] = product;
      return byId;
    }, Object.create(null));

    if (settings && Array.isArray(settings.build_products)) {
      sourceRows = settings.build_products;
    } else {
      sourceRows = [];
      ["wall", "ceiling"].forEach(function (surface) {
        var key = "build_" + surface + "_products";
        var legacyRows =
          settings && Array.isArray(settings[key]) ? settings[key] : [];

        legacyRows.forEach(function (row) {
          sourceRows.push(Object.assign({}, row, { surface: surface }));
        });
      });
    }

    return sourceRows.reduce(function (rows, sourceRow) {
      var product = productsById[sourceRow && sourceRow.product_id];
      if (!product) return rows;

      var row = Object.assign({}, sourceRow);
      var quantity = Number(row.quantity);
      row.quantity = Number.isFinite(quantity) ? Math.max(0, quantity) : 0;

      if (
        !row.surface ||
        !product.surfaces ||
        product.surfaces.indexOf(row.surface) === -1
      ) {
        row.surface = product.surfaces && product.surfaces[0];
      }

      rows.push(row);
      return rows;
    }, []);
  }

  function sanitizeMixRows(rows) {
    return (Array.isArray(rows) ? rows : []).map(function (row) {
      var quantity = Number(row && row.quantity);
      return Object.assign({}, row, {
        quantity: Number.isFinite(quantity)
          ? Math.max(0, Math.ceil(quantity))
          : 0,
      });
    });
  }

  function scaleMixRows(rows, totalUnits) {
    var sanitized = sanitizeMixRows(rows);
    var baseTotal = sanitized.reduce(function (sum, row) {
      return sum + row.quantity;
    }, 0);
    var units = Math.max(0, Math.floor(Number(totalUnits) || 0));

    if (!baseTotal || !units) {
      return sanitized.map(function (row) {
        return Object.assign({}, row, { quantity: 0 });
      });
    }

    return sanitized.map(function (row) {
      return Object.assign({}, row, {
        quantity: row.quantity
          ? Math.ceil((row.quantity / baseTotal) * units)
          : 0,
      });
    });
  }

  function buildProductMixTier(options) {
    var rows = sanitizeMixRows(options && options.rows);
    var calculateRt60 = options && options.calculateRt60;
    var targetRt60 = Number(options && options.targetRt60);
    var baseTotal = rows.reduce(function (sum, row) {
      return sum + row.quantity;
    }, 0);

    function evaluate(totalUnits) {
      var products = scaleMixRows(rows, totalUnits);
      var rt60 = Number(calculateRt60(products));
      return {
        products: products,
        rt60: Number.isFinite(rt60) ? Number(rt60.toFixed(2)) : 0,
      };
    }

    if (typeof calculateRt60 !== "function") {
      return { products: scaleMixRows(rows, 0), rt60: 0 };
    }

    if (!baseTotal || !Number.isFinite(targetRt60)) return evaluate(0);

    var zero = evaluate(0);
    if (zero.rt60 <= targetRt60) return zero;

    var MAX_UNITS = 100000;
    var low = 0;
    var high = 1;
    while (high < MAX_UNITS && evaluate(high).rt60 > targetRt60) {
      low = high;
      high = Math.min(MAX_UNITS, high * 2);
    }

    while (low + 1 < high) {
      var middle = Math.floor((low + high) / 2);
      if (evaluate(middle).rt60 <= targetRt60) high = middle;
      else low = middle;
    }

    return evaluate(high);
  }

  function serializeResultCards(cards) {
    return (Array.isArray(cards) ? cards : []).map(function (card) {
      return {
        key: String((card && card.key) || ""),
        label: String((card && card.label) || ""),
        products: (card && Array.isArray(card.products) ? card.products : []).map(
          function (product) {
            return {
              productId: String(
                (product && (product.productId || product.id)) || "",
              ),
              name: String((product && product.name) || ""),
              url: String((product && product.url) || ""),
              quantity: Math.max(
                0,
                Math.ceil(Number(product && product.quantity) || 0),
              ),
            };
          },
        ),
      };
    });
  }

  function buildProductShopUrl(product, quantity, resultCards) {
    if (!product || !product.url) return "#";
    var productUrl;
    try {
      productUrl = new URL(product.url, "https://feltright.com").toString();
    } catch (_error) {
      return "#";
    }
    var separator = productUrl.indexOf("?") === -1 ? "?" : "&";
    var url =
      productUrl +
      separator +
      "design_type=" +
      encodeURIComponent("Felt Right Design") +
      "&product_id=" +
      encodeURIComponent(product.productId || product.id || "") +
      "&product_name=" +
      encodeURIComponent(product.name || "") +
      "&quantity=" +
      encodeURIComponent(Math.max(0, Math.ceil(Number(quantity) || 0)));

    if (Array.isArray(resultCards) && resultCards.length) {
      url +=
        "&acoustic_result_cards=" +
        encodeURIComponent(JSON.stringify(serializeResultCards(resultCards)));
    }

    return url;
  }

  return {
    BANDS: BANDS,
    calculateNrc: calculateNrc,
    normalizeCatalog: normalizeCatalog,
    buildTierQuantities: buildTierQuantities,
    buildTierResults: buildTierResults,
    buildSelectionResult: buildSelectionResult,
    normalizeBuildRows: normalizeBuildRows,
    sanitizeMixRows: sanitizeMixRows,
    scaleMixRows: scaleMixRows,
    buildProductMixTier: buildProductMixTier,
    serializeResultCards: serializeResultCards,
    buildProductShopUrl: buildProductShopUrl,
  };
});
